import 'bootstrap/dist/css/bootstrap.min.css';
import { useGetAvailableCountries } from 'hooks/useGetAvailabeCountries';
import {
  BuyerRegistrationRequestModel,
  BuyerRegistrationUpdateDetailsModel,
} from 'models/buyer/buyerRegistration/BuyerRegistrationModels';
import { CountryModel } from 'models/shared/country/CountryModel';
import { ChangeEvent, MouseEventHandler, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { PlayFill } from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useFormikHookForBuyerRegistrationDetails } from './hooksBuyerRegistrationCompanyInfo';

type PropTypes = {
  nextStep: () => void;
  prevStep: MouseEventHandler;
  buyerRegistrationData: BuyerRegistrationRequestModel | null;
  setBuyerRegistrationData: (value: BuyerRegistrationRequestModel) => void;
  setLogoFile: (value: File | null) => void;
};

export const BuyerRegistrationCompanyInfo = ({
  nextStep,
  prevStep,
  buyerRegistrationData,
  setBuyerRegistrationData,
  setLogoFile,
}: PropTypes) => {
  const [company, setCompany] = useState<BuyerRegistrationUpdateDetailsModel>(
    {} as BuyerRegistrationUpdateDetailsModel
  );
  const [logo, setLogo] = useState<string | null>(null);

  const [countries, setCountries] = useState<CountryModel[]>([]);

  const { t } = useTranslation('components');

  useGetAvailableCountries(setCountries);

  const formik = useFormikHookForBuyerRegistrationDetails(
    company,
    setCompany,
    nextStep,
    buyerRegistrationData,
    setBuyerRegistrationData
  );

  useEffect(() => {
    buyerRegistrationData !== null &&
      setCompany(buyerRegistrationData?.buyerCompanyRegistrationModel);
  }, [buyerRegistrationData]);

  const handleLogoImageUpload = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    toast.dismiss();

    if (event.target.files) {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        if (selectedFile.size > 500 * 1024) {
          toast.error(t('account.err_ProfileImageSizeTooBig'));
          event.target.value = '';
          return;
        }
        const srcUrl = URL.createObjectURL(selectedFile);
        // const data = new FormData();
        // data.append('file', selectedFile);
        // const imageLocation = await IdentityApi.updateCustomerLogo(
        //   invitationToken,
        //   data
        // );

        setLogo(srcUrl);
        setLogoFile(selectedFile);

        // toast.info(t('account.inf_ProfileImageUploaded'));
      }
    }
  };

  return (
    <Card className='onboarding-intro-card'>
      <Card.Body>
        <h4 className='mb-3'>{t('companySettings.hd_CompanyInformation')}</h4>

        <Form noValidate>
          <Row>
            {/* <Col lg={2} className='text-lg-center text-sm-center'>
              <div className='mt-3'>
                <BuyerRegistrationProfileImageUpload
                  profileImage={logo}
                  setProfileImage={setLogo}
                  initials={`${
                    formik.values.name !== null &&
                    formik.values.name !== undefined
                      ? `${formik.values.name
                          .charAt(0)
                          .toUpperCase()}${formik.values.name
                          .charAt(1)
                          .toUpperCase()}`
                      : ''
                  }`}
                  handleImageUpload={handleLogoImageUpload}
                />
              </div>
            </Col> */}
            <Col className='mx-3'>
              <Form.Group className='mb-4' as={Row} controlId='validationName'>
                <Form.Label className='required'>
                  {t('companySettings.lbl_CompanyName')}
                </Form.Label>
                <Form.Control
                  type='text'
                  name='name'
                  onChange={formik.handleChange}
                  placeholder={t('companySettings.plh_CompanyName')}
                  value={formik.values.name}
                  isInvalid={formik.touched.name && formik.errors.name}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className='mb-4'
                as={Row}
                controlId='validationRegistryNumber'
              >
                <Form.Label className='required'>
                  {t('companySettings.lbl_RegisterNumber')}
                </Form.Label>
                <Form.Control
                  required
                  name='registryNumber'
                  type='text'
                  onChange={formik.handleChange}
                  placeholder={t('companySettings.plh_RegistryNumber')}
                  value={formik.values.registryNumber}
                  isInvalid={
                    formik.touched.registryNumber &&
                    !!formik.errors.registryNumber
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.registryNumber}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className='mb-4'
                as={Row}
                controlId='validationAddressLine1'
              >
                <Form.Label>{t('addressSettings.lbl_AddressLine1')}</Form.Label>
                <Form.Control
                  value={formik.values.addressLine1}
                  onChange={formik.handleChange}
                  name='addressLine1'
                  type='text'
                  placeholder={t('addressSettings.lbl_AddressLine1')}
                  isInvalid={
                    formik.touched.addressLine1 && !!formik.errors.addressLine1
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.addressLine1}
                </Form.Control.Feedback>
              </Form.Group>
              <Row className='mb-4'>
                <Form.Group
                  as={Col}
                  className='p-0 me-2'
                  controlId='validationPostalCode'
                >
                  <Form.Label>{t('addressSettings.lbl_PostalCode')}</Form.Label>
                  <Form.Control
                    value={formik.values.postalCode}
                    onChange={formik.handleChange}
                    type='text'
                    name='postalCode'
                    placeholder={t('addressSettings.plh_PostalCode')}
                    isInvalid={
                      formik.touched.postalCode && !!formik.errors.postalCode
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.postalCode}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} className='p-0' controlId='validationCity'>
                  <Form.Label>{t('addressSettings.lbl_City')}</Form.Label>
                  <Form.Control
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    type='text'
                    name='city'
                    placeholder={t('addressSettings.plh_City')}
                    isInvalid={formik.touched.city && !!formik.errors.city}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.city}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Form.Group className='mb-4' as={Row} controlId='validationPhone'>
                <Form.Label>{t('userSettings.lbl_Phone')}</Form.Label>
                <Form.Control
                  type='text'
                  placeholder={t('userSettings.plh_Phone')}
                  name='phone'
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  isInvalid={formik.touched.phone && !!formik.errors.phone}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.phone}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className='mb-4'
                as={Row}
                controlId='validationWebSite'
              >
                <Form.Label>{t('companySettings.lbl_Website')}</Form.Label>
                <Form.Control
                  type='text'
                  name='webSite'
                  placeholder={t('companySettings.plh_Website')}
                  value={formik.values.webSite}
                  onChange={formik.handleChange}
                  isInvalid={formik.touched.webSite && !!formik.errors.webSite}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.webSite}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className='mx-3'>
              <Form.Group
                className='mb-4'
                as={Row}
                controlId='validationShortName'
              >
                <Form.Label>{t('companySettings.lbl_ShortName')}</Form.Label>
                <Form.Control
                  type='text'
                  name='shortName'
                  placeholder={t('companySettings.plh_ShortName')}
                  value={formik.values.shortName}
                  onChange={formik.handleChange}
                  isInvalid={
                    formik.touched.shortName && !!formik.errors.shortName
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.shortName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className='mb-4'
                as={Row}
                controlId='validationVatNumber'
              >
                <Form.Label>{t('companySettings.lbl_VatNumber')}</Form.Label>
                <Form.Control
                  name='vatNumber'
                  type='text'
                  placeholder={t('companySettings.plh_VatNumber')}
                  value={formik.values.vatNumber}
                  onChange={formik.handleChange}
                  isInvalid={
                    formik.touched.vatNumber && !!formik.errors.vatNumber
                  }
                />

                <Form.Control.Feedback type='invalid'>
                  {formik.errors.vatNumber}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className='mb-4'
                as={Row}
                controlId='validationAddressLine2'
              >
                <Form.Label>{t('addressSettings.lbl_AddressLine2')}</Form.Label>
                <Form.Control
                  type='text'
                  placeholder={t('addressSettings.plh_AddressLine2')}
                  name='addressLine2'
                  value={formik.values.addressLine2}
                  onChange={formik.handleChange}
                  isInvalid={
                    formik.touched.addressLine2 && !!formik.errors.addressLine2
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.addressLine2}
                </Form.Control.Feedback>
              </Form.Group>
              <Row className='mb-4'>
                <Form.Group
                  as={Col}
                  className='p-0 me-2'
                  controlId='validationState'
                >
                  <Form.Label>{t('addressSettings.lbl_State')}</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder={t('addressSettings.plh_State')}
                    name='state'
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    isInvalid={formik.touched.state && !!formik.errors.state}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.state}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  className='p-0'
                  controlId='validationCountryCode'
                >
                  <Form.Label className='required'>
                    {t('addressSettings.lbl_Country')}
                  </Form.Label>
                  <Form.Select
                    name='countryCode'
                    required
                    value={
                      formik.values.countryCode !== undefined
                        ? formik.values.countryCode
                        : 'EE'
                    }
                    onChange={formik.handleChange}
                  >
                    {countries.map((country, index) => (
                      <option key={index} value={country.code}>
                        {country.shortName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Row>
              <Form.Group className='mb-4' as={Row} controlId='validationEmail'>
                <Form.Label className='required'>
                  {t('userSettings.lbl_Email')}
                </Form.Label>
                <Form.Control
                  required
                  name='email'
                  placeholder={t('userSettings.plh_Email')}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  isInvalid={formik.touched.email && !!formik.errors.email}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>

        <Button className='btn btn-eleczap float-start ms-3' onClick={prevStep}>
          <PlayFill className='iconPlayBack' />
          {t('common.btn_Back')}
        </Button>
        <Button
          className='btn btn-eleczap float-end mt-3'
          onClick={formik.handleSubmit}
        >
          {t('common.btn_Next')}{' '}
          {formik.isSubmitting ? (
            <Spinner animation='border' size='sm' />
          ) : (
            <PlayFill />
          )}
        </Button>
      </Card.Body>
    </Card>
  );
};
